import React from 'react';
import { 
  Users, 
  ShieldCheck, 
  BrainCircuit, 
  BarChart3, 
  Database, 
  Globe, 
  CheckCircle2, 
  ChevronRight,
  Mail,
  Upload,
  Link as LinkIcon,
  Sparkles
} from 'lucide-react';

const PlatformPage: React.FC = () => {
  return (
    <div className="pt-48 pb-20">
      <section className="px-6 max-w-7xl mx-auto mb-40">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-12">
            <h2 className="text-xs uppercase tracking-[0.4em] text-brand-blue font-bold">What is Boulai?</h2>
            <h3 className="text-5xl md:text-8xl font-serif italic tracking-tighter leading-[1] text-white">
              The engagement hub for modern governance.
            </h3>
            <p className="text-3xl md:text-4xl text-white leading-relaxed font-black">
              Boulai lets leaders engage directly with their people, save time, and make better decisions based on real-time, AI-powered insights.
            </p>
            <p className="text-2xl text-white font-bold leading-relaxed">
              Using secure engagement tools and built-in analysis, Boulai turns thousands of conversations into clear, actionable intelligence so leaders can act with confidence and build trust beyond election cycles.
            </p>
          </div>

          <div className="relative p-12 glass-island rounded-[4rem] border-white/10 overflow-hidden shadow-3xl">
            <div className="absolute top-0 right-0 p-10 opacity-5">
              <Database size={300} />
            </div>
            
            <div className="relative z-10 space-y-6 max-w-md mx-auto">
               <div className="text-center mb-10 flex flex-col items-center gap-4">
                  <Sparkles size={20} className="text-brand-blue" />
                  <span className="text-[11px] uppercase tracking-widest text-white font-black italic">It's as easy as 1-2-3</span>
               </div>
               
               {[
                 { label: 'Engagement', color: 'bg-brand-blue', icon: <Users size={20} /> },
                 { label: 'Verification', color: 'bg-brand-blue/80', icon: <ShieldCheck size={20} /> },
                 { label: 'Analysis', color: 'bg-brand-blue/60', icon: <BrainCircuit size={20} /> },
                 { label: 'Decisions', color: 'bg-white/10', icon: <BarChart3 size={20} />, border: true }
               ].map((item, i) => (
                 <div key={i} className="flex flex-col items-center">
                    <div className={`w-full p-8 rounded-3xl flex items-center justify-between transition-all hover:scale-105 cursor-default ${item.color} ${item.border ? 'border border-white/20' : ''}`}>
                      <div className="flex items-center space-x-6">
                        <div className="p-3 bg-black/20 rounded-xl">{item.icon}</div>
                        <span className="text-lg font-black uppercase tracking-widest text-white">{item.label}</span>
                      </div>
                    </div>
                    {i < 3 && <div className="h-4 w-px bg-transparent my-1"></div>}
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-40 px-6 bg-[#0d0d0d] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-32">
            <h3 className="text-2xl md:text-4xl font-black mb-8 tracking-tighter uppercase opacity-80 text-white">No extra tools. No consultants. No long setup.</h3>
            <p className="text-white text-xl max-w-2xl mx-auto font-bold opacity-60">One platform. Integrated intelligence. Easy as 1-2-3.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Polls & Discussions', desc: 'Engage thousands securely in real time.', icon: <Users /> },
              { title: 'Verified Participation', desc: 'Identity-based access prevents synthetic manipulation.', icon: <ShieldCheck /> },
              { title: 'AI Analysis', desc: 'Detect sentiment, concerns, and priorities instantly.', icon: <BrainCircuit /> },
              { title: 'Decision Dashboards', desc: 'Visual, exportable insights for leaders and teams.', icon: <BarChart3 /> }
            ].map((step, i) => (
              <div key={i} className="group p-12 glass-island rounded-[2.5rem] border-white/10 hover:border-brand-blue transition-all duration-700 bg-white/[0.01]">
                <div className="w-16 h-16 bg-brand-blue/10 rounded-2xl flex items-center justify-center mb-8 text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all shadow-xl shadow-brand-blue/10">
                  {step.icon}
                </div>
                <h4 className="text-2xl font-black mb-4 text-white uppercase tracking-tight">{step.title}</h4>
                <p className="text-lg text-white font-medium opacity-60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-24 items-start">
          <div className="space-y-16">
            <div>
              <h3 className="text-3xl md:text-5xl font-black mb-12 tracking-tighter leading-tight uppercase opacity-90 text-white">Compliance by Design.</h3>
              <p className="text-white text-2xl leading-relaxed font-bold">
                Boulai is built to onboard large populations quickly and securely. Integration is native, not an afterthought.
              </p>
            </div>

            <div className="space-y-10">
               <h4 className="text-[12px] uppercase tracking-[0.4em] font-black text-brand-blue">Invitation Methods</h4>
               <div className="grid gap-6">
                  {[
                    { icon: <Mail />, text: 'Organization email domains' },
                    { icon: <LinkIcon />, text: 'Secure login links' },
                    { icon: <Upload />, text: 'Bulk uploads (Excel, CSV, CRM)' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center space-x-6 p-8 glass-island rounded-3xl border-white/10 group hover:border-brand-blue transition-all">
                       <div className="text-brand-blue transition-transform group-hover:rotate-12">{item.icon}</div>
                       <span className="text-xl font-bold text-white uppercase tracking-tight">{item.text}</span>
                    </div>
                  ))}
               </div>
            </div>
          </div>

          <div className="glass-island p-16 rounded-[4rem] border-white/10 bg-brand-blue/[0.02] relative overflow-hidden group shadow-3xl">
            <div className="absolute top-0 right-0 p-10 opacity-[0.03] group-hover:scale-125 transition-transform duration-1000">
              <Globe size={400} />
            </div>
            
            <div className="relative z-10 space-y-12">
              <div className="flex items-center space-x-4 mb-8">
                <Globe className="text-brand-blue" size={32} />
                <h4 className="text-3xl font-black uppercase tracking-tighter text-white">Data Sovereignty</h4>
              </div>
              <p className="text-white text-2xl leading-relaxed font-bold">
                All data can be stored in-region or in-country, based on customer requirements. This ensures total compliance with local privacy, security, and procurement standards.
              </p>
              
              <div className="space-y-8">
                 <div className="p-8 glass-island rounded-3xl border-white/10 hover:border-brand-blue transition-all">
                    <div className="flex justify-between items-center mb-4">
                       <span className="text-sm font-black uppercase tracking-widest text-brand-blue">Example: Gulf Region Deployment</span>
                       <CheckCircle2 size={20} className="text-brand-blue" />
                    </div>
                    <p className="text-lg text-white font-bold">Abu Dhabi deployment: data stored strictly inside UAE borders.</p>
                 </div>
              </div>

              <div className="mt-20">
                <h5 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-white leading-tight opacity-70">Compliance with Transparency, Accessibility, and Sovereignty Standards.</h5>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-40 text-center">
         <div className="max-w-4xl mx-auto px-6">
            <a 
              href="mailto:mdahhan@boulai.net" 
              className="inline-flex items-center space-x-6 px-16 py-8 bg-brand-blue text-white font-black rounded-2xl uppercase text-xs tracking-[0.3em] hover:scale-105 transition-all shadow-3xl shadow-brand-blue/30"
            >
              <span>Schedule a walkthrough</span>
              <ChevronRight size={20} />
            </a>
         </div>
      </section>
    </div>
  );
};

export default PlatformPage;